<template>
	<app-page>
		<app-heading title="Booking Payments">
			<template v-slot:right>
				<app-button
					outlined
					small
					color="secondary"
					icon="fa-sync"
					@click="refresh = true"
				>Refresh</app-button>
			</template>
		</app-heading>
		<app-filter :value="hasFilters" @click="applyFilters()">
			<app-row no-padding>
				<app-cell cols="6" md="3">
					<input-select
						v-model="filters.status"
						label="Status"
						:items="statuses"
						clearable
						dense
					></input-select>
				</app-cell>
			</app-row>
		</app-filter>
		<app-row>
			<app-cell>
				<app-data-table
					:items="payments"
					:total="count"
					:refresh.sync="refresh"
					:headers="headers"
					:sort-by="'last_modified'"
					:sort-desc="true"
					:initial-state="tableState"
					@update="fetchPayments"
				>
					<template v-slot:[`booking.studeo_id`]="{ item }">
						<span v-if="item.booking.studeo_id">{{ item.booking.studeo_id }}</span>
						<span v-else class="warning--text">Not defined</span>
					</template>
					<template v-slot:[`booking.integration`]="{ item }">
						<div>{{ getIntegrationName(item.booking.integration) }}</div>
						<div>
							<i>{{ item.channel_name || '' }}</i>
						</div>
					</template>
					<template v-slot:[`booking.unit_code`]="{ item }">
						<div>{{item.booking.unit_code}}</div>
						<div><i>{{ getResortName(item.booking.studeo_resort) }}</i></div>
					</template>
					<template v-slot:status="{ item }">
							<span v-if="item.status === 0" class="warning--text">Pending</span>
							<span v-if="item.status === 1" class="success--text">Success</span>
							<span v-if="item.status === 2" class="error--text">Error</span>
							<span v-if="item.status === 3" class="primary--text">In Progress</span>
					</template>
					<template v-slot:total="{ item }">
						<div>{{ item.total | currency }}/{{ item.booking.amount.total | currency}}</div>
					</template>
					<template v-slot:last_modified="{ item }">
						<div>{{ item.last_modified | datetime }}</div>
						<div><i>{{ item.last_modified | datetime(true) }}</i></div>
					</template>
					<template v-slot:actions="{ item }">
						<app-button
							color="secondary"
							:to="{ name: 'studeo-booking-payment', params: { booking_payment: item._id } }"
							icon="fa-file-invoice-dollar"
							table
						>
							Transactions
						</app-button>
						<app-button
							color="secondary"
							icon="fa-sync"
							table
							@click="retryPayment(item)"
							:disabled="item.status !== 2"
						>
							Retry
						</app-button>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>
	</app-page>
</template>

<script>
import { filters, tableState } from '@/mixins'
import { mapAll } from '@/utils/async'
import { mapSelectOptions, dynamicNewMapEntries } from '@/utils/maps'

export default {
	mixins: [filters, tableState],
	async created() {
		this.resorts = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('resorts')
			.field(['name', 'studeo_id'])
			.limit(100)
			.get(), { value: 'studeo_id' })
		this.integrations = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('integrations')
			.field(['name'])
			.limit(100)
			.get())

		this.resortsMap = this.resorts.reduce((acc, resort) => ({ ...acc, [resort.value.toString()]: resort.text }), {})
		this.integrationsMap = this.integrations.reduce((acc, integration) => ({ ...acc, [integration.value]: integration.text }), {})
	},
	data() {
		return {
			payments: [],
			integrationsMap: {},
			resortsMap: {},
			channelsMap: {},
			count: -1,
			refresh: false,
			headers: [{
				text: 'Status',
				value: 'status',
				sortable: false,
			}, {
				text: 'External ID',
				value: 'booking.content.id',
				sortable: false,
			}, {
				text: 'Confirmation Number',
				value: 'booking.studeo_id',
				sortable: false,
			}, {
				text: 'Integration',
				value: 'booking.integration',
				sortable: false,
			}, {
				text: 'Unit',
				value: 'booking.unit_code',
				sortable: false,
			}, {
				text: 'Total',
				value: 'total',
				sortable: false,
			}, {
				text: 'Last modified',
				value: 'last_modified',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
			resorts: [],
			integrations: [],
			filters: {
				status: null,
			},
			defaultFilters: {},
			defaultTableSort: '-last_modified',
			statuses: [{
				text: 'Pending',
				value: 0,
			}, {
				text: 'Success',
				value: 1,
			}, {
				text: 'Failed',
				value: 2,
			}],
		}
	},
	methods: {
		getIntegrationName(id) {
			return this.integrationsMap[id] || 'UNDEFINED'
		},

		getResortName(id) {
			return this.resortsMap[id] || 'UNDEFINED'
		},

		getChannelName(resort, id) {
			return this.channelsMap[resort]
				&& this.channelsMap[resort][id]
				&& this.channelsMap[resort][id].name
				? this.channelsMap[resort][id].name
				: 'UNDEFINED'
		},

		async updateChannelsMap() {
			const split = this.bookings.reduce((acc, booking) => {
				if (!this.resortsMap[booking.studeo_resort]) {
					return acc
				}
				const resortId = booking.studeo_resort
				if (!acc[resortId]) {
					acc[resortId] = []
				}
				acc[resortId].push(booking)
				return acc
			}, {})

			await mapAll(split, async (bookings, resort) => {
				const resortStudeo = bookings[0].studeo_resort
				if (!this.channelsMap[resortStudeo]) {
					this.channelsMap[resortStudeo] = {}
				}

				const res = await dynamicNewMapEntries(
					bookings,
					'studeo_channel',
					this.channelsMap[resortStudeo],
					this.$ravello.request()
						.v(2)
						.to('resorts')
						.to(resort)
						.to('channels')
						.field(['studeo_id', 'name']),
					{ key: 'studeo_id' },
				)

				if (res.err) {
					this.$notifications.pushAlert('error', res.err.data ? res.err.data.message : res.err.message)
				}

				this.channelsMap[resortStudeo] = {
					...this.channelsMap[resortStudeo],
					...res.entries,
				}
			})

			this.bookings = this.bookings.map(booking => ({
				...booking,
				channel_name: this.getChannelName(booking.studeo_resort, booking.studeo_channel),
			}))
		},

		async fetchPayments(pagination) {
			this.updateTableState(pagination)
			const request = this.$ravello.graphql('bookingPayments')
				.fromPagination(pagination)
				.include(this.$ravello.graphql('booking')
					.fields(['content', 'studeo_id', 'integration', 'unit_code', 'studeo_resort', 'studeo_channel'])
					.include(this.$ravello.graphql('amount').field('total')))
				.fields(['_id', 'status', 'last_modified', 'total'])

			if (typeof this.appliedFilters.status !== 'undefined' && this.appliedFilters.status !== null) {
				request.filter('status', this.appliedFilters.status)
			}

			if (this.count === -1) {
				request.count('count')
			}

			try {
				this.payments = await request.query()
				if (this.count === -1) {
					this.count = request.results.count
				}

				// await this.updateChannelsMap()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}
		},

		async retryPayment(item) {
			item.status = 0
			try {
				await this.$ravello.request()
					.v(2)
					.to('bookingpayments')
					.to(item._id)
					.to('retry')
					.create()
				this.$notifications.pushAlert('success', 'Payment will be retried shortly')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
	},
}
</script>
